import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, EyeOff, Trash2, ShieldCheck, Quote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner, EmptyState, formatDate } from "@/components/shared";

const STATUS_TABS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "hidden", label: "Hidden" },
  { value: "all", label: "All" },
];

const STATUS_TONE = { pending: "amber", approved: "green", hidden: "muted", deleted: "red" };

export default function Vouches() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.vouches(tab === "all" ? undefined : tab);
      const list = Array.isArray(res) ? res : res?.vouches || [];
      setItems(list);
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (vouchId, action) => {
    setBusyId(vouchId);
    try {
      await api.vouchAction(vouchId, action);
      const msg = action === "approve" ? "Vouch approved" : action === "hide" ? "Vouch hidden" : "Vouch deleted";
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader title="Vouches" subtitle="Moderate member-to-member endorsements." />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList data-testid="vouch-tabs">
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`vouch-tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {error && (
        <div className="rounded-xl bg-destructive/15 ring-1 ring-destructive/40 px-4 py-3 text-sm mb-4" data-testid="vouches-error">
          {error}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : items.length === 0 && !error ? (
        <EmptyState icon={ShieldCheck} title="No vouches here" hint="Vouches in this status will appear here." />
      ) : (
        <div className="space-y-3" data-testid="vouches-list">
          {items.map((v) => {
            const voucher = v.voucher || {};
            const vouchee = v.vouchee || v.user || {};
            const isOfficial = (v.relationship === "platform") || /amoreska/i.test(v.voucher_name || voucher.name || "");
            return (
              <div key={v.vouch_id} className="rounded-2xl bg-card ring-1 ring-border/70 p-5" data-testid={`vouch-${v.vouch_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <Avatar src={voucher.photo} name={v.voucher_name || voucher.name} size={40} />
                    <div className="min-w-0">
                      <div className="text-sm">
                        <span className="font-medium">{v.voucher_name || voucher.name || "Someone"}</span>
                        <span className="text-muted-foreground"> vouched for </span>
                        <span className="font-medium">{vouchee.name || v.vouchee_id || v.user_id || "a member"}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <Tag tone={STATUS_TONE[v.status] || "muted"}>{v.status}</Tag>
                        {isOfficial && <Tag tone="primary"><ShieldCheck className="h-3 w-3" />Official</Tag>}
                        {v.relationship && !isOfficial && <Tag tone="muted">{v.relationship}</Tag>}
                        {(v.tags || []).map((t) => (
                          <Tag key={t} tone="blue">{t}</Tag>
                        ))}
                        {v.created_at && <span className="text-[11px] text-muted-foreground">{formatDate(v.created_at)}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {v.status !== "approved" && (
                      <Button variant="outline" size="sm" disabled={busyId === v.vouch_id} onClick={() => act(v.vouch_id, "approve")} data-testid={`approve-${v.vouch_id}`}>
                        <Check className="h-4 w-4" /> Approve
                      </Button>
                    )}
                    {v.status !== "hidden" && (
                      <Button variant="outline" size="sm" disabled={busyId === v.vouch_id} onClick={() => act(v.vouch_id, "hide")} data-testid={`hide-${v.vouch_id}`}>
                        <EyeOff className="h-4 w-4" /> Hide
                      </Button>
                    )}
                    <Button variant="destructive" size="sm" disabled={busyId === v.vouch_id} onClick={() => act(v.vouch_id, "delete")} data-testid={`delete-${v.vouch_id}`}>
                      <Trash2 className="h-4 w-4" /> Delete
                    </Button>
                  </div>
                </div>

                {v.note && (
                  <blockquote className="mt-3 border-l-2 border-primary/50 pl-3 py-1 text-sm text-muted-foreground italic flex gap-2">
                    <Quote className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {v.note}
                  </blockquote>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
